import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { isHiddenCmsSectionKey } from "@/modules/cms/domain/contracts";
import type { CmsSectionDTO } from "../api/client";
import {
  useCmsSectionsQuery,
  useDeleteCmsSection,
  useReorderCmsSections,
} from "../api/queries";
import { CmsSectionsTable } from "../ui/CmsSectionsTable";
import { CmsSectionsToolbar } from "../ui/CmsSectionsToolbar";

const DEFAULT_PAGE_KEY = "home";

export function CmsSectionsPage() {
  const nav = useNavigate();
  const search = useSearch({ from: "/app/cms/sections" }) as {
    pageKey: string;
  };

  const list = useCmsSectionsQuery(search.pageKey || DEFAULT_PAGE_KEY);
  const canManage = useActionPermission([
    "cms:create",
    "cms:update",
    "cms:delete",
  ]);
  const deleteSection = useDeleteCmsSection(search.pageKey);
  const reorderSections = useReorderCmsSections(search.pageKey);

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "ລຶບ Section",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ section ນີ້?",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      toast.promise(deleteSection.mutateAsync(id), {
        loading: "ກຳລັງລຶບ...",
        success: "ລຶບສຳເລັດ",
        error: "ລຶບລົ້ມເຫຼວ",
      });
    }
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0 || !list.data) return;
    const next = [...list.data];
    const a = next[index - 1];
    const b = next[index];
    if (!a || !b) return;
    next[index - 1] = b;
    next[index] = a;
    const order = next.map((s, i) => ({ id: s.id, sortOrder: i }));
    toast.promise(reorderSections.mutateAsync(order), {
      loading: "ກຳລັງປ່ຽນລຳດັບ...",
      success: "ປ່ຽນລຳດັບສຳເລັດ",
      error: "ປ່ຽນລຳດັບລົ້ມເຫຼວ",
    });
  };

  const handleMoveDown = (index: number) => {
    if (!list.data || index >= list.data.length - 1) return;
    const next = [...list.data];
    const a = next[index];
    const b = next[index + 1];
    if (!a || !b) return;
    next[index] = b;
    next[index + 1] = a;
    const order = next.map((s, i) => ({ id: s.id, sortOrder: i }));
    toast.promise(reorderSections.mutateAsync(order), {
      loading: "ກຳລັງປ່ຽນລຳດັບ...",
      success: "ປ່ຽນລຳດັບສຳເລັດ",
      error: "ປ່ຽນລຳດັບລົ້ມເຫຼວ",
    });
  };

  const handleEdit = (row: CmsSectionDTO) => {
    nav({ to: "/app/cms/sections/$id/edit", params: { id: row.id } });
  };

  const handleCreate = () => {
    nav({ to: "/app/cms/sections/new", search: { pageKey: search.pageKey } });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ສ່ວນຫນ້າເວັບ" },
          ]}
        />
      </Header>
      <Main>
        <CmsSectionsToolbar
          pageKey={search.pageKey || DEFAULT_PAGE_KEY}
          onPageKeyChange={(pageKey) => nav({ search: { ...search, pageKey } })}
          canManage={!!canManage}
          onCreate={handleCreate}
        />
        <div className="flex flex-col rounded-xl border bg-card">
          <CmsSectionsTable
            canManage={!!canManage}
            isLoading={list.isLoading}
            data={(list.data ?? []).filter(
              (row) => !isHiddenCmsSectionKey(row.sectionKey),
            )}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
          />
        </div>
      </Main>
    </>
  );
}
